import React from 'react';
import type { ExpensesApprovalAuthority } from '@this/domain/department/department/department';
import styled from 'styled-components';
import ApproversMemberList from './approver_members_list/approver_members_list';
import type User from '../../../../domain/user/user';
import type { WorkflowTypeKeys } from '../departments';

interface Props<T extends WorkflowTypeKeys> {
  i: number;
  s: number;
  workflowType?: T;
  selectedId: number;
  rawMembers: User[];
  members: User[];
  auth?: ExpensesApprovalAuthority;
  onSelect?: (i: number, s: number, member: User, workflowType?: T) => void;
  onReset?: (i: number, s: number, workflowType?: T) => void;
  onSelectExpenses?: (auth: ExpensesApprovalAuthority, member: User) => void;
  onSelectExpensesForMultistage?: (stageIndex: number, approverIndex: number, userId: number) => void;
  isDeletableApprover?: (i: number, s: number, workflowType?: T) => boolean;
}

interface State {
  showMemberList: boolean;
  showResetButton: boolean;
  selectedMembers: User[];
}

interface HTMLElementEvent<T extends EventTarget> extends Event {
  target: T;
}

class SelectApprovers<T extends WorkflowTypeKeys> extends React.Component<Props<T>, State> {
  private selectAreaRef = React.createRef<HTMLInputElement>();

  constructor(props: Props<T>) {
    super(props);
    this.state = {
      showMemberList: false,
      showResetButton: !!props.onReset,
      selectedMembers: []
    };

    this.toggleMemberList = this.toggleMemberList.bind(this);
    this.handleMemberSelect = this.handleMemberSelect.bind(this);
    this.handleMemberReset = this.handleMemberReset.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', {
      handleEvent: (e: HTMLElementEvent<HTMLElement>) => {
        this.handleClickEvent(e);
      }
    });
  }

  componentWillUnmount() {
    document.removeEventListener('click', {
      handleEvent: (e: HTMLElementEvent<HTMLElement>) => {
        this.handleClickEvent(e);
      }
    });
  }

  handleClickEvent(e: HTMLElementEvent<HTMLElement>) {
    if (this.selectAreaRef && this.selectAreaRef.current && !this.selectAreaRef.current.contains(e.target)) {
      this.setState({ showMemberList: false });
    }
  }

  handleMemberSelect(i: number, s: number, member: User, workflowType?: T) {
    this.setState({
      showMemberList: false
    });
    if (this.props.onSelectExpensesForMultistage) {
      this.props.onSelectExpensesForMultistage(s, i, member.id);
    } else if (this.props.auth && this.props.onSelectExpenses) {
      this.props.onSelectExpenses(this.props.auth, member);
    } else if (this.props.onSelect) {
      this.props.onSelect(i, s, member, workflowType);
    }
  }

  handleMemberReset() {
    const { s, i, workflowType } = this.props;
    this.setState({
      showMemberList: false
    });
    if (this.props.onReset) {
      this.props.onReset(i, s, workflowType);
    }
  }

  toggleMemberList(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    this.setState({
      showMemberList: !this.state.showMemberList
    });
  }

  render() {
    const rawMembers: User[] = this.props.rawMembers;
    const selectedMember: User | undefined = rawMembers.find(member => member.id === this.props.selectedId);
    try {
      return (
        <div className="select-approvers">
          <SelectApproversWrapper className="select-approvers__wrapper" ref={this.selectAreaRef}>
            <SelectApproversSelectedListItem
              className="select-approvers__selected-list__item"
              onClick={this.toggleMemberList}
            >
              {selectedMember ? (
                <ApproverMembersListItemInfo className="approver-members-list__item__info">
                  <ApproverMembersListItemPart className="approver-members-list__item-part">
                    {selectedMember.name}
                  </ApproverMembersListItemPart>
                  <ApproverMembersListItemPart className="approver-members-list__item-part">{`(${selectedMember.email})`}</ApproverMembersListItemPart>
                </ApproverMembersListItemInfo>
              ) : (
                <SelectApproversSelectedListMessage className="select-approvers__selected-list__message">
                  選択してください
                </SelectApproversSelectedListMessage>
              )}
              <SelectApproversSelectedListRight className="select-approvers__selected-list__right">
                {this.state.showResetButton && selectedMember && (
                  <SelectApproversSelectedListReset
                    className="select-approvers__selected-list__reset"
                    onClick={() => this.handleMemberReset()}
                  >
                    <img src="/images/close_search.png" />
                  </SelectApproversSelectedListReset>
                )}
                {this.state.showMemberList ? '▲' : '▼'}
              </SelectApproversSelectedListRight>
            </SelectApproversSelectedListItem>
            {this.state.showMemberList && (
              <div className="select-approvers__travelers-list">
                <ApproversMemberList<T>
                  {...this.props}
                  members={this.props.members}
                  selectedMembers={this.state.selectedMembers}
                  onSelect={this.handleMemberSelect}
                  isDeletableApprover={this.props.isDeletableApprover}
                />
              </div>
            )}
          </SelectApproversWrapper>
        </div>
      );
    } catch (e) {
      utils.sendErrorObject(e);
      return null;
    }
  }
}

const SelectApproversWrapper = styled.div`
  font-size: 13px;
  margin: 10px 0 5px;
  min-width: 300px;
  background-color: ${props => props.theme.fieldBgColor};
  border: solid 1px ${props => props.theme.grayBorderColor};
`;

const SelectApproversSelectedListItem = styled.div`
  display: flex;
  border: 2px solid ${props => props.theme.grayBorderColor};
  cursor: pointer;
`;

const ApproverMembersListItemInfo = styled.div`
  display: inline-block;
  flex-wrap: wrap;
  width: 97%;
  padding: 5px;
`;

const ApproverMembersListItemPart = styled.p`
  margin-right: 5px;
  display: inline-block;
`;

const SelectApproversSelectedListMessage = styled.p`
  color: black;
  width: 97%;
  display: inline-block;
  padding: 5px;
`;

const SelectApproversSelectedListRight = styled.div`
  margin: auto;
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  padding: 0 8px;
  line-height: 0;
`;

const SelectApproversSelectedListReset = styled.a`
  width: 2em;
  padding: 2px;
`;

export default SelectApprovers;
